import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Table, TableHead, TableBody, TableRow, TableCell, Paper, Box, TextField, TableSortLabel } from "@mui/material";
import { ThemeContext } from '../../../../context/ThemeContext';
import { getStatmentReport } from '../../../../api/request/dashboard/dashboard';
import { getCookies } from '../../../../services/Cookies';
import DeleteReport from './DeleteReport';
import createReportCell from '../../../tablecells/Report';
import { useSelector } from "react-redux";
import NewReport from './NewReport';
import { useTranslation } from 'react-i18next';
import { TableVirtuoso } from "react-virtuoso";
import NoDataTable from '../../../table/NoDataTable';
import toast from 'react-hot-toast';

export default function Report({ data = [] }) {
    const [reports, setReports] = useState([]);
    const { darkMode } = useContext(ThemeContext);
    const [open, setOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const { accountId: accountID } = useSelector(state => state.accountSummary)
    const accountId = accountID || localStorage.getItem("accountID") || localStorage.getItem("SelectedAccount");
    const clientId = getCookies('clientId');
    const { userDetail } = useSelector((state) => state.user);
    const rawOffset = getCookies('gmtOffset')
    const [searchQuery, setSearchQuery] = useState('');
    const imgSrc = darkMode ? '/hs-white.png' : '/hs-logo.png'
    const [sortConfig, setSortConfig] = useState({ key: 'datetime', direction: 'desc' });

    const convertToDecimal = (offset) => {
        const [hours, minutes] = offset.split(':');
        const sign = offset.startsWith('-') ? -1 : 1;
        return sign * (Math.abs(parseInt(hours)) + (parseInt(minutes) / 60));
    };

    const gmtOffsetToSend = convertToDecimal(rawOffset);
    const [selectedReportName, setSelectedReportName] = useState("");
    const handleOpen = () => setOpen(true);
    const { t } = useTranslation()

    const handleDeleteOpen = (report) => {
        setSelectedReportName(report.name);
        setDeleteOpen(true);
    };

    useEffect(() => {
        const storedReports = JSON.parse(localStorage.getItem(`savedReports_${accountId}`)) || [];
        setReports(storedReports);
    }, []);


    const handleCreateReport = async (fromDate, toDate, reportType) => {
        const requestData = {
            accountID: accountId,
            clientID: clientId,
            companyName: process.env.REACT_APP_COMPANY_NAME,
            firstName: userDetail.firstName || userDetail.username,
            lastName: userDetail.lastName || "",
            email: userDetail.email || "",
            phone: userDetail.phone || "",
            from: fromDate,
            gmtOffset: gmtOffsetToSend.toFixed(2),
            to: toDate,
        };

        try {
            const response = await getStatmentReport(requestData);
            if (response && response.data) {
                toast.success('Report grenerated')

                let reportContent = response.data;

                // Convert images to Base64 only if generating a PDF
                if (reportType === "pdf") {
                    const convertImageToBase64 = async (url) => {
                        try {
                            const response = await fetch(url);
                            const blob = await response.blob();
                            return new Promise((resolve) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve(reader.result);
                                reader.readAsDataURL(blob);
                            });
                        } catch (error) {
                            console.error("Error converting image to Base64:", error);
                            return null; // Return null on error
                        }
                    };

                    // Find and replace image URLs in the HTML with Base64 only for PDF
                    const imgRegex = /<img[^>]+src=["'](https:\/\/[^"']+)["'][^>]*>/g;
                    let matches = [...reportContent.matchAll(imgRegex)];

                    for (let match of matches) {
                        const imageUrl = match[1]; // Extract image URL
                        const base64Image = await convertImageToBase64(imageUrl);
                        if (base64Image) {
                            reportContent = reportContent.replace(imageUrl, base64Image); // Replace URL with Base64
                        } else {
                            reportContent = reportContent.replace(match[0], ""); // Remove image tag if conversion fails
                        }
                    }
                }

                const currentDate = new Date().toISOString().split("T")[0];
                const fileName = `${fromDate}_${toDate}_${currentDate}.${reportType}`;

                const newReport = {
                    name: fileName,
                    datetime: currentDate,
                    status: `${t('generated')}`,
                    type: reportType,
                    content: reportContent,
                };

                const updatedReports = [...reports, newReport];
                setReports(updatedReports);
                localStorage.setItem(`savedReports_${accountId}`, JSON.stringify(updatedReports));
            }
        } catch (error) {
            toast.error(error.response.data.message || 'Error generating report!');
            console.error("Error fetching report:", error);
        }
    };

    const handleDeleteReport = () => {
        if (!selectedReportName) return;
        const updatedReports = reports.filter(report => report.name !== selectedReportName);
        setReports(updatedReports);
        localStorage.setItem(`savedReports_${accountId}`, JSON.stringify(updatedReports));
        setDeleteOpen(false);
        setSelectedReportName("");
        toast.success('Report Deleted.')
    };
    const filteredReports = useMemo(() => {
        const search = searchQuery.toLowerCase();

        const filtered = reports.filter((report) =>
            report.name.toLowerCase().includes(search) ||
            report.status.toLowerCase().includes(search) ||
            report.datetime.toLowerCase().includes(search) ||
            report.type.toLowerCase().includes(search)
        );

        if (!sortConfig.key) return filtered;

        const sorted = [...filtered].sort((a, b) => {
            const key = sortConfig.key;
            const dir = sortConfig.direction === 'asc' ? 1 : -1;

            const valA = a[key] ?? '';
            const valB = b[key] ?? '';

            if (key === 'datetime') {
                const dateA = new Date(valA);
                const dateB = new Date(valB);
                return (dateA - dateB) * dir;
            }

            if (typeof valA === 'number' && typeof valB === 'number') {
                return (valA - valB) * dir;
            }

            return valA.toString().localeCompare(valB.toString()) * dir;
        });

        return sorted;
    }, [searchQuery, reports, sortConfig]);


    const handleSort = (key) => {
        const newDirection = (sortConfig.key === key && sortConfig.direction === 'asc') ? 'desc' : 'asc';
        setSortConfig({ key, direction: newDirection });
    };

    const columns = createReportCell({ handleOpen, handleDeleteOpen, setReports, t });
    const Row = useMemo(() => (index, item) => {
        return (
            <>
                {columns.map((col, colIndex) => (
                    <TableCell key={colIndex} sx={{ fontSize: "0.8rem", border: "none", padding: "5px", backgroundColor: darkMode ? "#151924" : "#fff" }}>
                        {col.field === `${t('action')}` ? col.render(item) : item[col.field] || "--"}
                    </TableCell>
                ))}
            </>
        );
    }, [columns, darkMode, t]);

    const fixedHeaderContent = () => {
        return (
            <TableRow sx={{ borderTop: `1px solid ${darkMode ? "var(--borderDark)" : "var(--borderLight)"}` }}>
                {columns.map((col, index) => (
                    <TableCell onClick={() => col.field !== t('action') && handleSort(col.field)} key={index} sx={{ fontSize: "0.85rem", fontWeight: 550, textTransform: "capitalize", p: 1, backgroundColor: darkMode ? "var(--background-color)" : "var(--white)", borderBottom: "1px solid rgba(224, 224, 224, 0.3)", borderRight: `1px solid ${darkMode ? "#444" : "var(--borderLight)"}`, cursor: col.field !== t('action') ? 'pointer' : 'default', }}>
                        <TableSortLabel active={sortConfig.key === col.field} direction={sortConfig.direction === 'asc' ? 'desc' : 'asc'}>
                            {col.title}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>);
    }

    const VirtuosoTableComponents = {
        Table: (props) => <Table {...props} sx={{ borderCollapse: "separate", tableLayout: "fixed" }} />,
        TableHead: React.forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
        TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
    };
    return (
        <>
            <Paper style={{ width: "100%", height: "calc(100% - 35px)", backgroundColor: darkMode ? "#151924" : "#fff", }}>
                {reports.length === 0 ? (<NoDataTable columns={columns} data={reports} noDataMessage={t('noData')} />
                ) : (
                    <Box sx={{ backgroundColor: darkMode ? "#151924" : "#fff", height: '100%' }}>
                        <TableVirtuoso data={filteredReports} fixedHeaderContent={fixedHeaderContent} itemContent={Row} components={VirtuosoTableComponents} />

                    </Box>)}
            </Paper>
            <div style={{ height: 35 }}>
                <div className='flex items-center px-1 justify-between'>
                    <TextField size="small" variant="outlined" placeholder={t("mobSearch")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ width: '250px', mx: 1, mt: 0.3 }} InputProps={{ sx: { fontSize: 13, padding: 0, height: 30, "& input": { padding: "0", paddingLeft: 1, height: "30px", }, }, }} />
                    <div className='flex items-center opacity-50'>
                        <small>{t('poweredby')} </small><a href="https://hybridsolutions.com/" target='_blank'><img src={imgSrc} alt="hs" style={{ height: darkMode ? 40 : 20 }} className='ms-2' />
                        </a>
                    </div>
                </div>
            </div>
            <NewReport open={open} handleClose={() => setOpen(false)} onCreateReport={handleCreateReport} />
            <DeleteReport open={deleteOpen} handleClose={() => setDeleteOpen(false)} handleDeleteReport={handleDeleteReport} />
        </>
    );
}
